import type { AxiosInstance } from 'axios'
import { wrapApiCall } from '@/lib/axios'
import { deserialize } from 'ts-jackson'
import {
  ClubInfo,
  UsageLimit,
  type BankFee,
  type CreateFitnessBankPayload,
  type FitnessBank,
  type MembershipCard,
  type UpdateBankFeePayload,
  type UpdateClubInfoPayload,
  type UpdateFitnessBankPayload,
  type UpdateMembershipCardPayload,
} from './model'

export const ClubInfoService = (client: AxiosInstance) => {
  return {
    async getClubInfo(): Promise<ClubInfo> {
      const { data } = await wrapApiCall(() => client.get<Record<string, unknown>>('/club-info'))
      return deserialize(data, ClubInfo)
    },
    async updateClubInfo(payload: UpdateClubInfoPayload): Promise<ClubInfo> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>('/club-info', payload)
      )
      return deserialize(data, ClubInfo)
    },
    async uploadClubPhoto(file: File): Promise<{ imageUrl: string }> {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await wrapApiCall(() =>
        client.post<{ imageUrl: string }>('/club-info/photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      )
      return data
    },
    async getUsageLimit(): Promise<UsageLimit> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>('/club-info/usage-limit')
      )
      return deserialize(data, UsageLimit)
    },

    // Membership Card
    async getMembershipCard(): Promise<MembershipCard> {
      const { data } = await wrapApiCall(() =>
        client.get<MembershipCard>('/club-info/membership-card')
      )
      return data
    },
    async updateMembershipCard(
      id: number,
      payload: UpdateMembershipCardPayload
    ): Promise<MembershipCard> {
      const { data } = await wrapApiCall(() =>
        client.patch<MembershipCard>(`/club-info/membership-card/${id}`, payload)
      )
      return data
    },
    async uploadMembershipCardLogo(id: number, file: File): Promise<MembershipCard> {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await wrapApiCall(() =>
        client.post<MembershipCard>(`/club-info/membership-card/${id}/logo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      )
      return data
    },
    async uploadMembershipCardBackground(id: number, file: File): Promise<MembershipCard> {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await wrapApiCall(() =>
        client.post<MembershipCard>(`/club-info/membership-card/${id}/background`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      )
      return data
    },
    async deleteMembershipCardLogo(id: number): Promise<MembershipCard> {
      const { data } = await wrapApiCall(() =>
        client.delete<MembershipCard>(`/club-info/membership-card/${id}/logo`)
      )
      return data
    },
    async deleteMembershipCardBackground(id: number): Promise<MembershipCard> {
      const { data } = await wrapApiCall(() =>
        client.delete<MembershipCard>(`/club-info/membership-card/${id}/background`)
      )
      return data
    },

    // Bank Fee
    async getBankFees(): Promise<BankFee[]> {
      const { data } = await wrapApiCall(() => client.get<BankFee[]>('/club-info/bank-fee'))
      return data
    },
    async updateBankFee(id: number, payload: UpdateBankFeePayload): Promise<BankFee> {
      const { data } = await wrapApiCall(() =>
        client.patch<BankFee>(`/club-info/bank-fee/${id}`, payload)
      )
      return data
    },

    // Fitness Bank (Club Bank Account)
    async getFitnessBanks(): Promise<FitnessBank[]> {
      const { data } = await wrapApiCall(() => client.get<FitnessBank[]>('/club-info/bank'))
      return data
    },
    async createFitnessBank(payload: CreateFitnessBankPayload): Promise<FitnessBank> {
      const { data } = await wrapApiCall(() => client.post<FitnessBank>('/club-info/bank', payload))
      return data
    },
    async updateFitnessBank(id: string, payload: UpdateFitnessBankPayload): Promise<FitnessBank> {
      const { data } = await wrapApiCall(() =>
        client.patch<FitnessBank>(`/club-info/bank/${id}`, payload)
      )
      return data
    },
    async deleteFitnessBank(id: string): Promise<{ success: boolean }> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success: boolean }>(`/club-info/bank/${id}`)
      )
      return data
    },
  }
}
